import { getAdobeAccessToken } from '../auth/adobeIms';
import { actionUrl } from './database';
import type { ChatMessage } from './aiAgent';
export interface BedrockResponse {
  text?: string;
  error?: string;
}
export type AgentResponse = BedrockResponse;
function getAuthHeaders(): Record<string, string> {
  const token = getAdobeAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? {
      Authorization: `Bearer ${token}`
    } : {})
  };
}
export async function callBedrockProxy(messages: ChatMessage[], signal?: AbortSignal): Promise<string> {
  return callProxyAction('ai-agent-ondemand-non-mcp', messages, signal);
}
export async function callAgentProxy(messages: ChatMessage[], signal?: AbortSignal): Promise<string> {
  return callProxyAction('ai-agent-ondemand-mcp', messages, signal);
}
async function callProxyAction(action: string, messages: ChatMessage[], signal?: AbortSignal): Promise<string> {
  const formattedMessages = messages.map(m => ({
    role: m.role,
    content: m.content
  }));
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60_000);

  // Chain the external signal if provided
  if (signal) {
    signal.addEventListener('abort', () => controller.abort());
  }
  try {
    const resp = await fetch(actionUrl(action), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        messages: formattedMessages
      }),
      signal: controller.signal
    });
    if (!resp.ok) {
      if (resp.status === 429) throw new Error('rate_limited');
      if (resp.status === 401 || resp.status === 403) throw new Error('auth_failed');
      throw new Error('proxy_error');
    }
    const json: BedrockResponse = await resp.json();
    return json.text || '';
  } finally {
    clearTimeout(timeoutId);
  }
}
export function getBedrockErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    if (error.name === 'AbortError') return '';
    switch (error.message) {
      case 'rate_limited':
        return 'Rate limited. Please try again in a moment.';
      case 'auth_failed':
        return 'Authentication failed. Please check your credentials.';
      case 'proxy_error':
        return 'AI service is temporarily unavailable. Please try again.';
      default:
        return 'Unable to reach the AI service. Please check your connection.';
    }
  }
  return 'An unexpected error occurred. Please try again.';
}